import { memo, useEffect, useRef, useState } from 'react';
import { placeSlursOnNoteheadSide } from '../lib/slurPlacement';
import { useTranslation } from 'react-i18next';
import type { Spelling } from '@corocanto/scoring';
import {
  type AbstractGraphicalExpression,
  GraphicalContinuousDynamicExpression,
  GraphicalInstantaneousTempoExpression,
  type GraphicalMeasure,
  type MusicPartManagerIterator,
  OpenSheetMusicDisplay,
  type GraphicalLine,
  type MusicSystem,
  type StaffLine,
  VexFlowGraphicalNote,
  VexFlowInstrumentBracket,
  VexFlowMeasure,
  VexFlowMusicSheetDrawer,
  type Note,
} from 'opensheetmusicdisplay';
import { pageTurnScroll } from './scoreFollow';
import styles from './ScoreView.module.css';

export interface CursorTarget {
  measureIndex: number;
  /** Quarter notes from the start of the written measure. */
  beatOffset: number;
}

interface Props {
  /** MusicXML text. */
  xml: string;
  /** Part ids (e.g. "P2") whose staves are emphasized; other parts' notes are drawn gray. */
  emphasisPartIds: readonly string[];
  /** Where the cursor should be, or null to hide it. */
  cursor?: CursorTarget | null;
  /** Written measure indexes of the selected range, tinted lightly. */
  selection?: { from: number; to: number } | null;
  /** Called with the written measure index when a bar is clicked. Without it, bars are not clickable. */
  onBarClick?: (measureIndex: number) => void;
  /** Printed measure numbers by index, for the bar buttons' labels. */
  measureNumbers: readonly string[];
  /** Colors for the emphasized parts' notes, keyed by noteColorKey() (results after a take). */
  noteColors?: Readonly<Record<string, string>>;
  /** Notes of the emphasized parts drawn gray, keyed by noteColorKey(): the other line of a divided part. */
  dimmedNotes?: readonly string[];
  /** Show only these parts (default: all). */
  visiblePartIds?: readonly string[];
  /** Draw only these printed measure numbers. */
  drawRange?: { from: number; to: number } | null;
  /** One long line that scrolls sideways (practice), instead of systems wrapped to the width. */
  horizontal?: boolean;
  /** i18n key for the bar buttons' accessible names, with {{bar}} (default: start playback there). */
  barLabelKey?: string;
  /** Called with a colored note's key (noteColorKey) when it is clicked. Without it, notes are not clickable. */
  onNoteClick?: (key: string) => void;
  /** The key of the note to ring: the one the note panel shows. */
  selectedNoteKey?: string | null;
  /** Accessible names of the clickable notes, by key. */
  noteLabels?: Readonly<Record<string, string>>;
}

import { noteColorKey } from '../lib/noteKey';

export { noteColorKey };

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Layout {
  bars: (Box & { measureIndex: number })[];
  bands: Box[];
  notes: (Box & { key: string })[];
}

interface CursorEntry {
  beatOffset: number;
  iterator: MusicPartManagerIterator;
}

const DIMMED = '#8A9099';
/** OSMD lengths are in units of 10 px at zoom 1. */
const UNIT_PX = 10;

/** OSMD's note names (NoteEnum) to letters; its octaves are MusicXML's minus 3 (Pitch.OctaveXmlDifference). */
const STEP_OF: Readonly<Record<number, string>> = { 0: 'C', 2: 'D', 4: 'E', 5: 'F', 7: 'G', 9: 'A', 11: 'B' };

/** A drawn note's written pitch, as in the MusicXML (null for a rest). */
function writtenSpelling(note: Note): Spelling | null {
  const pitch = note.Pitch;
  if (!pitch || note.isRest()) return null;
  return { step: STEP_OF[pitch.FundamentalNote] ?? '?', alter: pitch.AccidentalHalfTones, octave: pitch.Octave + 3 };
}

/** A drawn note's key (noteColorKey); the second half of a tied note takes the key of the note it continues. Null for a rest. */
function keyOfNote(note: Note, partId: string): string | null {
  const start = note.NoteTie && note.NoteTie.StartNote !== note ? note.NoteTie.StartNote : note;
  const spelling = writtenSpelling(start);
  if (!spelling) return null;
  const entry = start.ParentStaffEntry;
  return noteColorKey(partId, entry.VerticalContainerParent.ParentMeasure.measureListIndex, entry.Timestamp.RealValue * 4, spelling);
}

/**
 * Hit areas over the drawn noteheads whose keys are in `keys` (the colored notes of a take), in
 * pixels relative to the score element, measured from the SVG elements OSMD points to. In a
 * chord, heads and notes are matched from the top (highest pitch) down.
 */
function noteLayout(osmd: OpenSheetMusicDisplay, host: HTMLElement, keys: ReadonlySet<string>): Layout['notes'] {
  if (keys.size === 0) return [];
  const origin = host.getBoundingClientRect();
  const out: Layout['notes'] = [];
  for (const staves of osmd.GraphicSheet.MeasureList) {
    for (const measure of staves) {
      if (!measure) continue;
      const partId = measure.ParentStaff.ParentInstrument.IdString;
      for (const staffEntry of measure.staffEntries) {
        for (const voiceEntry of staffEntry.graphicalVoiceEntries) {
          const drawn = voiceEntry.notes.filter((n): n is VexFlowGraphicalNote => n instanceof VexFlowGraphicalNote && !n.sourceNote.isRest());
          if (drawn.length === 0) continue;
          const heads = [...((drawn[0] as VexFlowGraphicalNote).getNoteheadSVGs() ?? [])]
            .map((el) => el.getBoundingClientRect())
            .filter((r) => r.width > 0)
            .sort((a, b) => a.top - b.top);
          const byPitch = [...drawn].sort((a, b) => (b.sourceNote.Pitch?.getHalfTone() ?? 0) - (a.sourceNote.Pitch?.getHalfTone() ?? 0));
          byPitch.forEach((note, i) => {
            const key = keyOfNote(note.sourceNote, partId);
            const head = heads[Math.min(i, heads.length - 1)];
            if (!key || !keys.has(key) || !head) return;
            const width = Math.max(20, head.width + 8);
            const height = Math.max(24, head.height + 10);
            out.push({
              key,
              x: head.left - origin.left + head.width / 2 - width / 2,
              y: head.top - origin.top + head.height / 2 - height / 2,
              width,
              height,
            });
          });
        }
      }
    }
  }
  return out;
}

/**
 * Before rendering: gray out the notes of other parts (only when a part is emphasized) and the
 * dimmed notes of the emphasized parts (the other line of a divided part), and color the
 * emphasized parts' notes from noteColors. The second half of a tied note takes the color of the
 * note it continues. Beams and flags follow the stems (ColorBeams, ColorFlags).
 */
function colorNotes(
  osmd: OpenSheetMusicDisplay,
  emphasis: readonly string[],
  noteColors: Readonly<Record<string, string>> | undefined,
  dimmed: ReadonlySet<string>,
) {
  const dimOthers = emphasis.length > 0;
  for (const measure of osmd.Sheet.SourceMeasures) {
    for (const container of measure.VerticalSourceStaffEntryContainers) {
      for (const entry of container.StaffEntries) {
        if (!entry) continue;
        const partId = entry.ParentStaff.ParentInstrument.IdString;
        const emphasized = emphasis.includes(partId);
        for (const voiceEntry of entry.VoiceEntries) {
          if (!emphasized) {
            if (!dimOthers) continue;
            voiceEntry.StemColor = DIMMED;
            for (const note of voiceEntry.Notes) note.NoteheadColor = DIMMED;
            continue;
          }
          if (!noteColors && dimmed.size === 0) continue;
          let allDimmed = voiceEntry.Notes.length > 0;
          for (const note of voiceEntry.Notes) {
            const key = keyOfNote(note, partId);
            if (!key) {
              allDimmed = false;
              continue;
            }
            if (dimmed.has(key)) {
              note.NoteheadColor = DIMMED;
              continue;
            }
            allDimmed = false;
            const color = noteColors?.[key];
            if (color) {
              note.NoteheadColor = color;
              voiceEntry.StemColor = color;
            }
          }
          if (allDimmed) voiceEntry.StemColor = DIMMED;
        }
      }
    }
  }
}

/**
 * Gray one drawn element and everything inside it: the fill of the element itself, and every
 * fill and stroke its parts already have (attributes or inline style). Strokes are never added
 * where there were none, so noteheads and text do not gain outlines.
 */
function dimElement(node: Node | null | undefined) {
  if (!(node instanceof SVGElement) || node.dataset['dimmed']) return;
  node.dataset['dimmed'] = 'true';
  if (node.getAttribute('fill') !== 'none') node.setAttribute('fill', DIMMED);
  if (node.style.fill && node.style.fill !== 'none') node.style.fill = DIMMED;
  for (const el of [node, ...node.querySelectorAll<SVGElement>('*')]) {
    for (const attr of ['fill', 'stroke'] as const) {
      const value = el.getAttribute(attr);
      if (value && value !== 'none') el.setAttribute(attr, DIMMED);
      const inline = el.style.getPropertyValue(attr);
      if (inline && inline !== 'none') el.style.setProperty(attr, DIMMED);
    }
  }
}

/**
 * OSMD draws each staff's bar (its lines, clef, key and time signatures, barlines and notes) into
 * one SVG group, then the bar's connectors (the piano's barlines joining its two staves), but keeps
 * no reference to them. Record the elements one draw adds to the drawing context's current group.
 * Braces and brackets are drawn inside a group the drawer opens for them; record that group.
 * Both only record references; drawing is unchanged.
 */
type DrawContext = { parent?: Element };
type Recorded = { corocantoNodes?: Element[]; corocantoGroup?: Element };
const measureDrawer = VexFlowMeasure.prototype as unknown as { draw(ctx: DrawContext): void; corocantoRecords?: boolean };
if (!measureDrawer.corocantoRecords) {
  const draw = measureDrawer.draw;
  measureDrawer.draw = function (this: Recorded, ctx: DrawContext) {
    const parent = ctx?.parent;
    const before = parent ? parent.children.length : 0;
    draw.call(this, ctx);
    if (parent) this.corocantoNodes = Array.from(parent.children).slice(before);
  };
  measureDrawer.corocantoRecords = true;
}
const bracketDrawer = VexFlowInstrumentBracket.prototype as unknown as { draw(ctx: DrawContext): void; corocantoRecords?: boolean };
if (!bracketDrawer.corocantoRecords) {
  const draw = bracketDrawer.draw;
  bracketDrawer.draw = function (this: Recorded, ctx: DrawContext) {
    if (ctx?.parent) this.corocantoGroup = ctx.parent;
    draw.call(this, ctx);
  };
  bracketDrawer.corocantoRecords = true;
}

/**
 * The staff furniture in one staff's bar group: its lines, clef, key and time signatures and
 * barlines, i.e. the direct children lying on the staff. Marks the renderer draws above the staff
 * in the same group (rehearsal marks, voltas, tempo) belong to everyone and are left out. Only
 * reads (getBBox), so all of them can be measured before anything is recolored.
 */
function staffFurniture(group: Element, measure: unknown): Element[] {
  const stave = (measure as { getVFStave?: () => { getYForLine(line: number): number; getNumLines(): number } }).getVFStave?.();
  if (!stave) return [];
  const top = stave.getYForLine(0) - 2;
  const bottom = stave.getYForLine(stave.getNumLines() - 1) + 2;
  return Array.from(group.children).filter((child) => {
    if (!(child instanceof SVGGraphicsElement) || child.classList.contains('vf-stavenote')) return false;
    const box = child.getBBox();
    const middle = box.y + box.height / 2;
    return middle >= top && middle <= bottom;
  });
}

/**
 * A brace or bracket is the other parts' only if none of the staves it joins is emphasized
 * (the piano's brace, but not a bracket around all the voices). Its VexFlow connector names its
 * top and bottom staves.
 */
function bracketIsOthers(bracket: unknown, system: MusicSystem, emphasized: (line: StaffLine) => boolean): boolean {
  const connector = (bracket as { vexflowConnector?: { top_stave?: unknown; bottom_stave?: unknown } }).vexflowConnector;
  const staveOf = (line: StaffLine) => (line.Measures[0] as unknown as { getVFStave?: () => unknown } | undefined)?.getVFStave?.();
  const lines = system.StaffLines;
  const top = lines.findIndex((l) => staveOf(l) === connector?.top_stave);
  const bottom = lines.findIndex((l) => staveOf(l) === connector?.bottom_stave);
  if (top < 0 || bottom < 0) return false;
  return lines.slice(Math.min(top, bottom), Math.max(top, bottom) + 1).every((l) => !emphasized(l));
}

/**
 * OSMD draws lyric extender lines ("la ____") without keeping the drawn element, unlike every other
 * element we gray. Keep it in the field OSMD already declares for it (GraphicalLine.SVGElement),
 * once, for all scores. It only records a reference; drawing is unchanged.
 */
const lineDrawer = VexFlowMusicSheetDrawer.prototype as unknown as {
  drawGraphicalLine(line: GraphicalLine, width: number, color?: string): Node;
  corocantoKeepsLines?: boolean;
};
if (!lineDrawer.corocantoKeepsLines) {
  const draw = lineDrawer.drawGraphicalLine;
  lineDrawer.drawGraphicalLine = function (this: unknown, line: GraphicalLine, width: number, color?: string) {
    const node = draw.call(this, line, width, color);
    if (line && node && !line.SVGElement) line.SVGElement = node;
    return node;
  };
  lineDrawer.corocantoKeepsLines = true;
}

/** Tempo and rehearsal marks belong to everyone and stay black, whichever staff carries them. */
function isSharedExpression(expression: AbstractGraphicalExpression): boolean {
  // OSMD does not export its tempo expression classes; every tempo expression ("♩ = 90", "rit.",
  // "a tempo") has ParentMultiTempoExpression. Metronome and rehearsal marks are not drawn as
  // staff expressions at all, so they are never reached here.
  const source: object | undefined = expression.SourceExpression;
  return expression instanceof GraphicalInstantaneousTempoExpression || (!!source && 'ParentMultiTempoExpression' in source);
}

/**
 * After rendering: gray everything else OSMD drew for the parts that are not emphasized, through
 * OSMD's own references to the drawn elements (never by position): notes and rests with their
 * accidentals, dots and articulations, ties, slurs, lyrics with hyphens and extender lines,
 * dynamics and hairpins, and text written for that part. Staff lines, clefs, key and time
 * signatures, barlines, bar numbers, part names, tempo and rehearsal marks stay black. Runs once
 * per render, never during playback.
 */
function dimOtherParts(osmd: OpenSheetMusicDisplay, emphasis: readonly string[]) {
  if (emphasis.length === 0) return;
  const isEmphasized = (line: StaffLine) => emphasis.includes(line.ParentStaff.ParentInstrument.IdString);
  // Measure first (getBBox), recolor afterwards, so the browser lays the score out only once.
  const furniture: Element[] = [];
  for (const page of osmd.GraphicSheet.MusicPages) {
    for (const system of page.MusicSystems) {
      for (const line of system.StaffLines) {
        if (isEmphasized(line)) continue;
        for (const measure of line.Measures) {
          for (const node of (measure as unknown as Recorded | undefined)?.corocantoNodes ?? []) {
            // The bar's own group: its staff furniture. Anything else it drew: the piano's connectors.
            if (node.classList.contains('vf-measure')) furniture.push(...staffFurniture(node, measure));
            else furniture.push(node);
          }
        }
      }
    }
  }
  for (const element of furniture) dimElement(element);

  for (const page of osmd.GraphicSheet.MusicPages) {
    for (const system of page.MusicSystems) {
      for (const bracket of [...system.InstrumentBrackets, ...system.GroupBrackets]) {
        if (bracketIsOthers(bracket, system, isEmphasized)) dimElement((bracket as Recorded).corocantoGroup);
      }
      for (const line of system.StaffLines) {
        if (isEmphasized(line)) continue;
        for (const slur of line.GraphicalSlurs) dimElement(slur.SVGElement);
        for (const dash of line.LyricsDashes) dimElement(dash.SVGNode);
        for (const extender of line.LyricLines) dimElement(extender.SVGElement);
        for (const expression of line.AbstractExpressions) {
          if (isSharedExpression(expression)) continue;
          dimElement(expression.Label?.SVGNode);
          if (expression instanceof GraphicalContinuousDynamicExpression) {
            for (const hairpin of expression.Lines) dimElement(hairpin.SVGElement);
          }
        }
        for (const measure of line.Measures) {
          for (const entry of measure?.staffEntries ?? []) {
            for (const tie of entry.GraphicalTies) dimElement(tie.SVGElement);
            for (const lyric of entry.LyricsEntries) dimElement(lyric.GraphicalLabel?.SVGNode);
            for (const voiceEntry of entry.graphicalVoiceEntries) {
              for (const note of voiceEntry.notes) {
                if (note instanceof VexFlowGraphicalNote) dimElement(note.getSVGGElement());
              }
            }
          }
        }
      }
    }
  }
}

/** Cursor positions by written measure, each with a clone of OSMD's iterator to jump to. */
function indexCursor(osmd: OpenSheetMusicDisplay): Map<number, CursorEntry[]> {
  const index = new Map<number, CursorEntry[]>();
  osmd.cursor.reset();
  const it = osmd.cursor.iterator.clone();
  for (let guard = 0; !it.EndReached && guard < 200_000; guard++) {
    const measureIndex = it.CurrentMeasureIndex;
    const measure = it.CurrentMeasure;
    if (measure) {
      const beatOffset = Math.round((it.CurrentSourceTimestamp.RealValue - measure.AbsoluteTimestamp.RealValue) * 4 * 1e6) / 1e6;
      let list = index.get(measureIndex);
      if (!list) {
        list = [];
        index.set(measureIndex, list);
      }
      if (!list.some((e) => Math.abs(e.beatOffset - beatOffset) < 1e-6)) list.push({ beatOffset, iterator: it.clone() });
    }
    it.moveToNext();
  }
  for (const list of index.values()) list.sort((a, b) => a.beatOffset - b.beatOffset);
  return index;
}

/** After the user scrolls the score by hand, automatic following waits this long. */
const USER_SCROLL_PAUSE_MS = 4000;
/** Space kept between the cursor and the edge of the score box. */
const FOLLOW_MARGIN_PX = 24;

/**
 * Scroll the score's own box (never the page) so the cursor is visible. Does nothing while the
 * cursor is already in view, so the score only moves when playback reaches a new system.
 */
function keepCursorInView(box: HTMLElement, cursorElement: HTMLElement | undefined) {
  if (!cursorElement) return;
  const boxRect = box.getBoundingClientRect();
  const cursorRect = cursorElement.getBoundingClientRect();
  if (cursorRect.height === 0) return;
  const above = cursorRect.top < boxRect.top + FOLLOW_MARGIN_PX;
  const below = cursorRect.bottom > boxRect.bottom - FOLLOW_MARGIN_PX;
  if (!above && !below) return;
  box.scrollTo({ top: box.scrollTop + cursorRect.top - boxRect.top - FOLLOW_MARGIN_PX, behavior: 'smooth' });
}

/**
 * Horizontal layout: page turns. The cursor moves freely through most of the view; when it passes
 * 80 % the score's box (never the page) slides one page on, and the cursor starts again near the
 * left edge (see pageTurnScroll).
 */
function keepCursorInViewHorizontally(box: HTMLElement, cursorElement: HTMLElement | undefined) {
  if (!cursorElement) return;
  const boxRect = box.getBoundingClientRect();
  const cursorRect = cursorElement.getBoundingClientRect();
  if (cursorRect.width === 0 && cursorRect.height === 0) return;
  const target = pageTurnScroll(cursorRect.left - boxRect.left, box.clientWidth, box.scrollLeft);
  if (target !== null) box.scrollTo({ left: target, behavior: 'smooth' });
}

/** Bar hit areas and the emphasized staves' bands, in pixels relative to the score element. */
function measureLayout(osmd: OpenSheetMusicDisplay, emphasis: readonly string[]): Omit<Layout, 'notes'> {
  const scale = UNIT_PX * osmd.zoom;
  const staffHeight = osmd.EngravingRules.StaffHeight;
  const bars: Layout['bars'] = [];
  const bands: Box[] = [];
  osmd.GraphicSheet.MeasureList.forEach((staves, measureIndex) => {
    const present = staves.filter((m): m is GraphicalMeasure => !!m?.PositionAndShape);
    if (present.length === 0) return;
    const xs = present.map((m) => m.PositionAndShape.AbsolutePosition.x);
    const ys = present.map((m) => m.PositionAndShape.AbsolutePosition.y);
    const width = Math.max(...present.map((m) => m.PositionAndShape.Size.width));
    const top = Math.min(...ys) - 2;
    const bottom = Math.max(...ys) + staffHeight + 2;
    bars.push({
      measureIndex,
      x: Math.min(...xs) * scale,
      y: top * scale,
      width: width * scale,
      height: (bottom - top) * scale,
    });
    present.forEach((m, staffIndex) => {
      if (!emphasis.includes(m.ParentStaff.ParentInstrument.IdString)) return;
      const y = m.PositionAndShape.AbsolutePosition.y;
      // The band runs from just above this staff to just above the next one (lyrics included).
      const nextY = present[staffIndex + 1]?.PositionAndShape.AbsolutePosition.y ?? y + staffHeight + 6;
      bands.push({
        x: m.PositionAndShape.AbsolutePosition.x * scale,
        y: (y - 1.5) * scale,
        width: m.PositionAndShape.Size.width * scale,
        height: (nextY - y) * scale,
      });
    });
  });
  return { bars, bands };
}

/**
 * The score, rendered by OpenSheetMusicDisplay, with the chorist's staff emphasized, a cursor
 * that follows playback and clickable bars. The cursor is placed by written measure and beat
 * offset, because OSMD's iteration order and our unrolled timeline differ around repeats.
 */
function ScoreViewComponent({
  xml,
  emphasisPartIds,
  cursor = null,
  selection = null,
  onBarClick,
  measureNumbers,
  noteColors,
  dimmedNotes,
  visiblePartIds,
  drawRange,
  horizontal = false,
  barLabelKey = 'practice.selectBar',
  onNoteClick,
  selectedNoteKey = null,
  noteLabels,
}: Props) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const lastUserScrollRef = useRef(0);
  const hostRef = useRef<HTMLDivElement>(null);
  const osmdRef = useRef<OpenSheetMusicDisplay | null>(null);
  const indexRef = useRef<Map<number, CursorEntry[]>>(new Map());
  const currentRef = useRef<CursorEntry | null>(null);
  const [layout, setLayout] = useState<Layout>({ bars: [], bands: [], notes: [] });
  const notesClickable = !!onNoteClick;
  const [status, setStatus] = useState<'rendering' | 'ready' | 'error'>('rendering');
  const emphasisKey = emphasisPartIds.join(',');
  const colorsKey = noteColors ? JSON.stringify(noteColors) : '';
  const dimmedKey = dimmedNotes && dimmedNotes.length > 0 ? JSON.stringify(dimmedNotes) : '';
  const visibleKey = visiblePartIds?.join(',') ?? '';
  const rangeKey = drawRange ? `${drawRange.from}-${drawRange.to}` : '';
  const narrowed = selection && (selection.from > 0 || selection.to < measureNumbers.length - 1) ? selection : null;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    const emphasis = emphasisKey ? emphasisKey.split(',') : [];
    const osmd = new OpenSheetMusicDisplay(host, {
      backend: 'svg',
      autoResize: false,
      drawTitle: false,
      drawSubtitle: false,
      drawComposer: false,
      drawLyricist: false,
      drawCredits: false,
      drawPartNames: true,
      drawPartAbbreviations: true,
      drawMeasureNumbers: true,
      coloringEnabled: true,
      colorStemsLikeNoteheads: true,
      // OSMD's own following calls scrollIntoView, which also scrolls the page. We scroll only
      // the score's box instead (see keepCursorInView).
      followCursor: false,
      // A thin line just before the current notes.
      cursorsOptions: [{ type: 1, color: '#2457C5', alpha: 0.9, follow: false }],
      renderSingleHorizontalStaffline: horizontal,
      ...(rangeKey ? { drawFromMeasureNumber: Number(rangeKey.split('-')[0]), drawUpToMeasureNumber: Number(rangeKey.split('-')[1]) } : {}),
    });
    // The practice score sits right under the controls: little room above the first staff.
    if (horizontal) {
      const rules = osmd.EngravingRules;
      rules.PageTopMargin = 1;
      rules.PageBottomMargin = 1;
      // In one long line, each staff's lyrics sit under its lowest note anywhere in the song, and
      // the next staff clears its highest note: keep the gaps around them small (notes and lyrics
      // keep their size), so all six staves of an SATB song with piano fit on a 1080p screen.
      rules.MinSkyBottomDistBetweenStaves = 0.4;
      rules.LyricsYMarginToBottomLine = 0.1;
      rules.VerticalBetweenLyricsDistance = 0.15;
    }
    osmdRef.current = osmd;
    setStatus('rendering');

    const clickableKeys = new Set(notesClickable && colorsKey ? Object.keys(JSON.parse(colorsKey) as Record<string, string>) : []);
    const render = () => {
      osmd.render();
      dimOtherParts(osmd, emphasis);
      indexRef.current = indexCursor(osmd);
      currentRef.current = null;
      osmd.cursor.hide();
      setLayout({ ...measureLayout(osmd, emphasis), notes: noteLayout(osmd, host, clickableKeys) });
    };

    osmd
      // Slurs on the notehead side (lib/slurPlacement.ts): MuseScore exports them without a side.
      .load(placeSlursOnNoteheadSide(xml))
      .then(() => {
        if (disposed) return;
        if (visibleKey) {
          const visible = visibleKey.split(',');
          for (const instrument of osmd.Sheet.Instruments) instrument.Visible = visible.includes(instrument.IdString);
        }
        colorNotes(
          osmd,
          emphasis,
          colorsKey ? (JSON.parse(colorsKey) as Record<string, string>) : undefined,
          new Set(dimmedKey ? (JSON.parse(dimmedKey) as string[]) : []),
        );
        render();
        setStatus('ready');
      })
      .catch(() => {
        if (!disposed) setStatus('error');
      });

    // Re-render when the width changes (OSMD lays out systems for the available width).
    // One horizontal line does not depend on the width.
    let width = host.clientWidth;
    let timer = 0;
    const observer = new ResizeObserver(() => {
      if (horizontal || host.clientWidth === width || !osmd.IsReadyToRender()) return;
      width = host.clientWidth;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!disposed) render();
      }, 150);
    });
    observer.observe(host);

    return () => {
      disposed = true;
      observer.disconnect();
      window.clearTimeout(timer);
      osmd.clear();
      // OSMD's clear() keeps its drawing surface in the host: remove it, or the next drawing (a
      // new part to emphasize, new note colours) lands under the old one.
      host.replaceChildren();
      osmdRef.current = null;
    };
  }, [xml, emphasisKey, colorsKey, dimmedKey, visibleKey, rangeKey, horizontal, notesClickable]);

  // Move the cursor to the latest OSMD position at or before the target.
  useEffect(() => {
    const osmd = osmdRef.current;
    if (!osmd || status !== 'ready') return;
    if (!cursor) {
      osmd.cursor.hide();
      currentRef.current = null;
      return;
    }
    const entries = indexRef.current.get(cursor.measureIndex);
    if (!entries || entries.length === 0) return;
    let entry = entries[0] as CursorEntry;
    for (const e of entries) if (e.beatOffset <= cursor.beatOffset + 1e-6) entry = e;
    if (entry === currentRef.current) return;
    currentRef.current = entry;
    osmd.cursor.iterator = entry.iterator.clone();
    if (osmd.cursor.Hidden) osmd.cursor.show();
    osmd.cursor.update();
    const wrap = wrapRef.current;
    if (wrap && Date.now() - lastUserScrollRef.current > USER_SCROLL_PAUSE_MS) {
      if (horizontal) keepCursorInViewHorizontally(wrap, osmd.cursor.cursorElement);
      else keepCursorInView(wrap, osmd.cursor.cursorElement);
    }
  }, [cursor, status, layout, horizontal]);

  // Scrolling the score by hand pauses the automatic following for a moment.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const mark = () => {
      lastUserScrollRef.current = Date.now();
    };
    const markScrollbar = (e: PointerEvent) => {
      if (e.target === wrap) mark();
    };
    wrap.addEventListener('wheel', mark, { passive: true });
    wrap.addEventListener('touchmove', mark, { passive: true });
    wrap.addEventListener('keydown', mark);
    wrap.addEventListener('pointerdown', markScrollbar);
    return () => {
      wrap.removeEventListener('wheel', mark);
      wrap.removeEventListener('touchmove', mark);
      wrap.removeEventListener('keydown', mark);
      wrap.removeEventListener('pointerdown', markScrollbar);
    };
  }, []);

  return (
    <div ref={wrapRef} className={`${styles.wrap} ${horizontal ? styles.wrapHorizontal : ''}`} data-testid="score" data-status={status}>
      {status === 'rendering' && <p className={styles.message}>{t('practice.scoreLoading')}</p>}
      {status === 'error' && <p className={styles.message}>{t('practice.scoreError')}</p>}
      <div className={`${styles.layers} ${horizontal ? styles.horizontal : ''}`}>
        {layout.bands.map((b, i) => (
          <div key={`band-${i}`} className={styles.band} data-testid="emphasis-band" style={{ left: b.x, top: b.y, width: b.width, height: b.height }} />
        ))}
        <div ref={hostRef} className={styles.osmd} />
        {onBarClick &&
          layout.bars.map((b) => {
          // The whole song selected marks nothing: only a narrower range is tinted.
          const selected = narrowed && b.measureIndex >= narrowed.from && b.measureIndex <= narrowed.to;
          return (
            <button
              key={`bar-${b.measureIndex}`}
              type="button"
              className={`${styles.bar} ${selected ? styles.selected : ''}`}
              style={{ left: b.x, top: b.y, width: b.width, height: b.height }}
              aria-label={t(barLabelKey, { bar: measureNumbers[b.measureIndex] ?? b.measureIndex + 1 })}
              data-measure={measureNumbers[b.measureIndex]}
              data-selected={selected ? 'true' : undefined}
              onClick={() => onBarClick(b.measureIndex)}
            />
          );
        })}
        {onNoteClick &&
          layout.notes.map((n, i) => (
            <button
              key={`note-${i}`}
              type="button"
              className={`${styles.note} ${n.key === selectedNoteKey ? styles.noteSelected : ''}`}
              style={{ left: n.x, top: n.y, width: n.width, height: n.height }}
              aria-label={noteLabels?.[n.key] ?? t('results.noteButtonPlain')}
              aria-pressed={n.key === selectedNoteKey}
              data-testid="score-note"
              data-key={n.key}
              onClick={() => onNoteClick(n.key)}
            />
          ))}
      </div>
    </div>
  );
}

export const ScoreView = memo(ScoreViewComponent);
